import {
  DocumentData,
  FieldValue,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import {sanitizeForWrite, substituteServerTimestamp} from '#common/firebase/converters';
import {Entity, EntityWithMetadata} from '#common/models/Entity';

/**
 * Checks if a value is a Firebase type that should be preserved during sanitization.
 */
const isPreservedType = (value: unknown): boolean =>
  value instanceof Date || value instanceof Timestamp || value instanceof FieldValue;

export class EntityConverter<T extends EntityWithMetadata<Entity>> implements FirestoreDataConverter<T> {
  fromFirestore(snapshot: QueryDocumentSnapshot<DocumentData, DocumentData>, options: SnapshotOptions): T {
    const data = snapshot.data(options);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const converted = Object.keys(data).reduce((converted, key) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      converted[key] = this.translateForRead(data[key as keyof typeof data]);
      return converted as T;
    }, {} as any); // eslint-disable-line @typescript-eslint/no-explicit-any
    return {...converted, id: snapshot.id} as T;
  }

  toFirestore(data: T): DocumentData {
    const withTimestamps = substituteServerTimestamp(data, serverTimestamp, isPreservedType);
    return sanitizeForWrite(withTimestamps, isPreservedType);
  }

  /**
   * Performs any necessary conversion to translate a Firestore value to JavaScript.
   * @param value the Firestore value.
   * @returns the converted value, or the original value if no conversion was necessary.
   */
  private translateForRead(value: unknown): unknown {
    if (value instanceof Timestamp) {
      return value.toDate();
    }
    return value;
  }
}
